import {
  Sim, TRUCK_TRAVEL_TIME, TRUCK_LOAD_WINDOW, CARGO_KINDS,
} from '../../game/simulation';
import { ICON } from '../icons';

/**
 * Top-center trip indicator — Farm ←──truck──→ Town.
 *
 * The truck icon's position along the bar reflects its journey state, and the
 * icon is mirrored on the return leg so the cab visibly points toward the
 * depot. State→bar mapping:
 *
 *   idle      ─ truck off-screen at depot; tiny "next arrival" nub grows
 *   loading   ─ truck parked at 0% with a pulsing border + cargo hint
 *   departed  ─ truck slides 0% → 100% (farm → town), facing right
 *   returning ─ truck slides 100% → 0% (town → farm), MIRRORED to face left
 */
export class TripBar {
  private root: HTMLDivElement;
  private lastStatus: string = '';

  constructor(parent: HTMLElement, private sim: Sim) {
    this.root = document.createElement('div');
    this.root.className = 'ui-tripbar';
    this.root.innerHTML = `
      <span class="ui-tripbar__icon ui-tripbar__icon--farm" title="Your farm">${ICON.farm}</span>
      <div class="ui-tripbar__track">
        <div class="ui-tripbar__fill" data-id="fill"></div>
        <div class="ui-tripbar__dot" data-id="dot">${ICON.truck}</div>
      </div>
      <span class="ui-tripbar__icon ui-tripbar__icon--town" title="Town market">${ICON.town}</span>
      <div class="ui-tripbar__label" data-id="label">Loading…</div>
    `;
    parent.appendChild(this.root);
  }

  /** Total items currently on the truck (manual loads + drained platform). */
  private cargoTotal(): number {
    const t = this.sim.state.truck;
    let n = 0;
    for (const k of CARGO_KINDS) n += t.cargo[k] ?? 0;
    return n;
  }

  update(_dt: number) {
    const t = this.sim.state.truck;
    let progress = 0;
    let label = '';
    let toneClass = '';
    // The cab faces right (+1) or left (-1). The truck asset points right.
    let facing: 1 | -1 = 1;

    switch (t.status) {
      case 'idle': {
        progress = 0;
        const items = this.cargoTotal();
        label = items > 0
          ? `Truck parked — ${items} item${items === 1 ? '' : 's'} loaded, ready to send`
          : `Truck parked at the depot — ready to load`;
        toneClass = 'idle';
        facing = 1;
        break;
      }
      case 'loading': {
        progress = 0;
        const items = this.cargoTotal();
        const cargoStr = items > 0 ? ` · ${items} loaded` : '';
        label = `Loading at depot — ${Math.ceil(t.timer)}s left (${TRUCK_LOAD_WINDOW}s window)${cargoStr}`;
        toneClass = 'loading';
        facing = 1;
        break;
      }
      case 'departed': {
        // timer counts DOWN from TRUCK_TRAVEL_TIME to 0 → progress 0 → 1.
        const elapsed = TRUCK_TRAVEL_TIME - t.timer;
        progress = Math.max(0, Math.min(1, elapsed / TRUCK_TRAVEL_TIME));
        const items = this.cargoTotal();
        const itemsStr = items > 0 ? `${items} item${items === 1 ? '' : 's'}` : 'cargo';
        const payoutHint = t.returnPayload > 0 ? ` for ${t.returnPayload} coins` : '';
        label = `Hauling ${itemsStr} to town — ${Math.ceil(t.timer)}s${payoutHint}`;
        toneClass = 'departed';
        facing = 1;
        break;
      }
      case 'returning': {
        // timer counts DOWN from TRUCK_TRAVEL_TIME to 0 → position should
        // slide from town (1) back to farm (0). So position = timer/T.
        // (As timer->0, position->0 = farm.)
        progress = Math.max(0, Math.min(1, t.timer / TRUCK_TRAVEL_TIME));
        const items = t.returnItems ?? 0;
        const itemsStr = items > 0 ? ` (${items} item${items === 1 ? '' : 's'})` : '';
        label = `Returning with ${t.returnPayload} coins${itemsStr} — ${Math.ceil(t.timer)}s away`;
        toneClass = 'returning';
        facing = -1;
        break;
      }
    }

    const fill = this.root.querySelector<HTMLElement>('[data-id="fill"]')!;
    const dot = this.root.querySelector<HTMLElement>('[data-id="dot"]')!;
    const lbl = this.root.querySelector<HTMLElement>('[data-id="label"]')!;

    // Snap position instantly across status transitions so the truck doesn't
    // animate through either endpoint when flipping legs. CSS `transition` is
    // suppressed via a class for one frame on status change.
    if (t.status !== this.lastStatus) {
      dot.classList.add('ui-tripbar__dot--snap');
      // Re-enable transitions on the next frame.
      requestAnimationFrame(() => dot.classList.remove('ui-tripbar__dot--snap'));
      this.lastStatus = t.status;
    }

    const pct = Math.max(0, Math.min(1, progress)) * 100;
    // Fill always grows from the farm side (0%) to wherever the truck is.
    fill.style.width = `${pct}%`;
    dot.style.left = `${pct}%`;
    // Mirror the truck cab for the return leg. Keep the dot centered on its
    // position by composing the existing centering transform with scaleX.
    dot.style.transform = `translate(-50%, -50%) scaleX(${facing})`;
    lbl.textContent = label;
    this.root.dataset.tone = toneClass;

  }
}
